import { ExpectedError } from "../expectedError";
import {
  VOLUME_NAME_RE,
  type VolumeAccess,
  type VolumeConcurrency,
  type VolumeDirective,
  type VolumeScope,
} from "./types";

export function parseVolumeSpec(spec: string): VolumeDirective {
  const [name, options] = splitVolumeSpec(spec);
  if (!VOLUME_NAME_RE.test(name))
    throw new ExpectedError(
      `Invalid volume name ${JSON.stringify(name)}. Names must match ${VOLUME_NAME_RE}.`
    );
  return { name, ...parseVolumeOptions(options, spec) };
}

function splitVolumeSpec(spec: string): [string, string | undefined] {
  const colon = spec.indexOf(":");
  if (colon === -1) return [spec, undefined];
  return [spec.slice(0, colon), spec.slice(colon + 1)];
}

function parseVolumeOptions(
  rawOptions: string | undefined,
  line: string
): Omit<VolumeDirective, "name"> {
  let scope: VolumeScope = "branch";
  let access: VolumeAccess = "rw";
  let concurrency: VolumeConcurrency = "shared";
  if (!rawOptions) return { scope, access, concurrency };

  for (const option of rawOptions.split(",")) {
    if (option === "ro") {
      access = "ro";
      continue;
    }
    if (option === "rw") {
      access = "rw";
      continue;
    }
    const [key, value, extra] = option.split("=");
    if (extra !== undefined || !key || value === undefined) {
      throw new ExpectedError(
        `Invalid volume option ${JSON.stringify(option)} in ${line}.`
      );
    }
    if (key === "scope" && (value === "branch" || value === "run")) {
      scope = value;
      continue;
    }
    if (key === "access" && (value === "rw" || value === "ro")) {
      access = value;
      continue;
    }
    if (
      key === "concurrency" &&
      (value === "shared" || value === "exclusive")
    ) {
      concurrency = value;
      continue;
    }
    throw new ExpectedError(
      `Invalid volume option ${JSON.stringify(option)} in ${line}.`
    );
  }

  return { scope, access, concurrency };
}

// The orchestrator parses stored specs again, so a spec is stored in the one form both parsers read the same way.
export function formatVolumeSpec({
  name,
  scope,
  access,
  concurrency,
}: VolumeDirective): string {
  const options = [
    access === "ro" && "ro",
    scope === "run" && "scope=run",
    concurrency === "exclusive" && "concurrency=exclusive",
  ].filter(Boolean);
  return options.length > 0 ? `${name}:${options.join(",")}` : name;
}
